import { memo } from 'react';
import { condSeverityOrder } from '../engine/conditions';
import { isCrowd } from '../engine/bodyShape';
import ConditionIcon from './ConditionIcon.jsx';
import Badge from './Badge';
import BadgeRow from './BadgeRow';
import bodyStyles from '../styles/BodyMap.module.css';
import { buildTargetTraitItems } from './bodyMapModel';
import { figureFor } from '../engine/plate/figureFor';
import BodyPlate from './plate/BodyPlate';
import PlateCrowd from './plate/PlateCrowd';
import PlateVehicle from './plate/PlateVehicle';
import PlateGoo from './plate/PlateGoo';
import usePlateWatch, { PlateWatch } from './plate/usePlateWatch';
import PlateObject from './plate/PlateObject';

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

export { buildTargetTraitItems };

/** The trait badges, as the row draws them. */
const traitBadges = (items) =>
  items.map((t, i) => <Badge key={i} variant="trait" label={t.label} value={t.value} size={28} />);

/** Condition icons, worst first, clickable to remove. */
const conditionIcons = (sorted, onRemoveCondition) =>
  sorted.map((sc, i) => (
    <ConditionIcon
      key={i}
      code={sc.code}
      size={28}
      onClick={onRemoveCondition ? () => onRemoveCondition(sc.idx) : undefined}
    />
  ));

/** The target's traits as a row under the health bar (plan plate-unboxed); nothing when it has none. */
export function TargetTraitRow({ target, sourceCharacter }) {
  const items = buildTargetTraitItems(target, sourceCharacter);
  if (items.length === 0) return null;
  const badges = traitBadges(items);
  return (
    <BadgeRow label="Traits" full={badges}>
      <div data-row-group="" data-testid="bodymap-trait-badges">
        {badges}
      </div>
    </BadgeRow>
  );
}

/** The conditions as a row under the traits, worst first, a click removing one; nothing when there are none. */
export function ConditionRow({ target, onRemoveCondition }) {
  const sorted = sortedConditions(target);
  if (sorted.length === 0) return null;
  const icons = conditionIcons(sorted, onRemoveCondition);
  return (
    <BadgeRow label="Conditions" full={icons}>
      <div data-row-group="" data-testid="bodymap-condition-icons">
        {icons}
      </div>
    </BadgeRow>
  );
}

/** Conditions worst-first, keeping their original index for removal. */
function sortedConditions(target) {
  return [...(target.conditions || [])]
    .map((c, i) => ({ code: c, idx: i }))
    .sort((a, b) => condSeverityOrder(a.code) - condSeverityOrder(b.code));
}

/**
 * @param {object} [targeting] The attacker's sights on this body
 *   (v8.243.0): `{ selectedId, calledShot, penalty, onEngageZone,
 *   onClear }`. Renders a SightRing per zone; clicking one engages or
 *   clears the called shot there - the targeting console's one-gesture
 *   semantics on the map itself. Null = no sights (the actor's own map
 *   never gets them; locks pass null).
 * @param {function} [onLocationContextMenu] RIGHT-click on a location
 *   shape - armour editing since v8.243.0 (left-click belongs to the
 *   sights now; right-click already means "the other thing" app-wide).
 * @param {object} [figureSheet] The sheet whose traits pick a person's plate figure - the combatant's own, which a
 *   statblock has too (its tab presents `sourceCharacter` as null). Defaults to `sourceCharacter`.
 * @param {'target' | 'actor'} [side] Whose colours a person's plate wears: the auspex red of a target, or the
 *   actor's phosphor (the actor's own status). Effects keep the target's colours either way.
 * @param {object} [impact] What just happened to this body (engine/impacts `Impact`): the plate plays a new one once.
 * @param {string} [combatantId] Whose body this is: a plate of a combatant is watched by the fuzzer's plate oracle
 *   (usePlateWatch); without one it is not.
 */
function BodyMap({
  target,
  onLocationContextMenu,
  sourceCharacter,
  variant = 'standalone',
  targeting = null,
  side = 'target',
  figureSheet = sourceCharacter,
  impact = null,
  combatantId = null,
}) {
  const fill = variant === 'fill';
  const watch = usePlateWatch(combatantId, impact);
  // A CROWD OF ANY SHAPE, not just of people. This read the legacy
  // `horde` body type alone, so a horde of vehicles fell through to the
  // hit-location map and drew one hull (v8.45.0).
  const map = isCrowd(target) ? (
    <CrowdMap target={target} sheet={figureSheet ?? null} side={side} fill={fill} impact={impact} />
  ) : (
    <LocationMap
      target={target}
      sheet={figureSheet ?? null}
      side={side}
      fill={fill}
      onLocationContextMenu={onLocationContextMenu}
      targeting={targeting}
      impact={impact}
      combatantId={combatantId}
    />
  );
  return (
    <div className={fill ? bodyStyles.mapFill : bodyStyles.mapRow}>
      <PlateWatch.Provider value={watch}>{map}</PlateWatch.Provider>
    </div>
  );
}

/** A crowd of any body is the plate's crowd of what it is made of (plan body-map P5, P8). */
function CrowdMap({ target, sheet, side, fill, impact }) {
  return <PlateCrowd body={figureFor(target, sheet)} target={target} side={side} fill={fill} impact={impact} />;
}

/**
 * One body, as its plate (plan body-map P2, P6, P7): a person as the figure their traits give, a vehicle by its kind,
 * the amorphous mass as the settled goo.
 */
function LocationMap({ target, sheet, side, fill, onLocationContextMenu, targeting, impact, combatantId }) {
  const body = figureFor(target, sheet);
  const common = { target, side, fill, onLocationContextMenu, impact };
  if (body.kind === 'vehicle') return <PlateVehicle kind={body.vehicle} targeting={targeting} {...common} />;
  if (body.kind === 'goo') return <PlateGoo sheet={sheet} {...common} />;
  if (body.kind === 'object') return <PlateObject sheet={sheet} combatantId={combatantId} {...common} />;
  return <BodyPlate figure={body.figure} targeting={targeting} sheet={sheet} {...common} />;
}

export default memo(BodyMap);
